<template>
	<div class="interface-settings lok-scrollbar">
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input type="checkbox" id="town_levels" v-model="townLevels" />
				<label for="town_levels">{{
					translate("ui.show_town_levels")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="show_main_settlement_name_label"
					v-model="isMainSettlementNameLabelVisible"
				/>
				<label for="show_main_settlement_name_label">
					{{ translate("ui.show_capital_label") }}
				</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="confirm_messages"
					v-model="confirmGemsMessages"
				/>
				<label for="confirm_messages">{{
					translate("ui.confirm_messages_when_building_for_gems")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="confirm_messages_upgrades"
					v-model="confirmGemsMessagesUpgrades"
				/>
				<label for="confirm_messages_upgrades">{{
					translate("ui.confirm_messages_premium_upgrade")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="confirm_messages_shop"
					v-model="confirmGemsMessagesShop"
				/>
				<label for="confirm_messages_shop">{{
					translate("ui.confirm_messages_shop_gems")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="income_panel"
					v-model="incomePanel"
				/>
				<label for="income_panel">{{
					translate("ui.show_income_panel")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="exploration_speed"
					v-model="explorationSpeed"
				/>
				<label for="exploration_speed">{{
					translate("ui.show_exploration_speed")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="save_message"
					v-model="gameSaveMessage"
				/>
				<label for="save_message">{{
					translate("ui.show_game_saved_message")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="exploration_progress"
					v-model="explorationProgress"
				/>
				<label for="exploration_progress">{{
					translate("ui.show_exploration_progress")
				}}</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input type="checkbox" id="menu_texts" v-model="menuTexts" />
				<label for="menu_texts">
					{{ translate("ui.show_menu_texts") }}
				</label>
			</div>
		</div>
		<div class="interface-settings__row">
			<div class="interface-settings__row-left">
				<input
					type="checkbox"
					id="arrow_to_settlement_switch"
					v-model="arrowsToSettlementSwitch"
				/>
				<label for="arrow_to_settlement_switch">
					{{ translate("ui.show_arrows_to_settlement_switch") }}
				</label>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import SoundService from "@/game/SoundService";
import useSettingsStore from "@/store/useSettingsStore";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { markRaw, ref, watch } from "vue";

const soundService = markRaw(SoundService.getInstance());

const settingsStore = useSettingsStore();

const {
	showTownLevels,
	showMainSettlementNameLabel,
	showConfirmGemsMessages,
	showConfirmGemsMessagesUpgrades,
	showConfirmGemsMessagesShop,
	showIncomePanel,
	showExplorationSpeed,
	showGameSavedMessage,
	showExplorationProgress,
	showMenuTexts,
	showArrowsToSettlementSwitch,
} = storeToRefs(settingsStore);

const townLevels = ref<boolean>(showTownLevels.value);
const isMainSettlementNameLabelVisible = ref<boolean>(
	showMainSettlementNameLabel.value,
);
const confirmGemsMessages = ref<boolean>(showConfirmGemsMessages.value);
const confirmGemsMessagesUpgrades = ref<boolean>(
	showConfirmGemsMessagesUpgrades.value,
);
const confirmGemsMessagesShop = ref<boolean>(showConfirmGemsMessagesShop.value);
const incomePanel = ref<boolean>(showIncomePanel.value);
const explorationSpeed = ref<boolean>(showExplorationSpeed.value);
const gameSaveMessage = ref<boolean>(showGameSavedMessage.value);
const explorationProgress = ref<boolean>(showExplorationProgress.value);
const menuTexts = ref<boolean>(showMenuTexts.value);
const arrowsToSettlementSwitch = ref<boolean>(
	showArrowsToSettlementSwitch.value,
);

const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

watch(
	(): boolean => townLevels.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowTownLevels(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => isMainSettlementNameLabelVisible.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowMainSettlementNameLabel(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => confirmGemsMessages.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowConfirmGemsMessages(value);
	},
	{ immediate: true },
);
watch(
	(): boolean => confirmGemsMessagesUpgrades.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowConfirmGemsMessagesUpgrades(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => confirmGemsMessagesShop.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowConfirmGemsMessagesShop(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => incomePanel.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowIncomePanel(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => explorationSpeed.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowExplorationSpeed(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => gameSaveMessage.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowGameSavedMessage(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => explorationProgress.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowExplorationProgress(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => menuTexts.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowMenuTexts(value);
	},
	{ immediate: true },
);

watch(
	(): boolean => arrowsToSettlementSwitch.value,
	(value: boolean): void => {
		emitGenericClick();
		settingsStore.setShowArrowsToSettlementSwitch(value);
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.interface-settings {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 1rem;
	padding: 1.25rem;

	&__row {
		display: flex;
		justify-content: space-between;
		width: 100%;
		font-size: 0.813rem;

		&-left {
			display: flex;
			align-items: center;
			color: #fff3dd;
			line-height: 1rem;
			input[type="checkbox"] {
				margin-right: 0.35rem;
			}
			label {
				cursor: pointer;
				margin-bottom: 0.15rem;
			}
		}
	}
}
</style>
